import { Plus, ShoppingBasket, AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/useStore';
import { GramIcon } from './GramIcon';
import { motion } from 'framer-motion';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { memo } from 'react';
import type { ReactNode } from 'react';
import { AnimatedLogoText } from './AnimatedLogoText';
import { LottieAppIcon } from './LottieAppIcon';
import { usernameMarketApi } from '@/utils/api';
import { socket } from '@/utils/socket';


interface BalanceBarProps {
    onDepositClick: () => void;
    onHistoryClick?: () => void;
    onPurchasesClick?: () => void;
    onRulesClick?: () => void;
    logoVariant?: 'kite' | 'lottie';
    logoActive?: boolean;
    logoIcon?: ReactNode;
    showLogo?: boolean;
}

export const BalanceBar = memo(({ onDepositClick, onHistoryClick, onPurchasesClick, onRulesClick, logoVariant = 'kite', logoActive = true, logoIcon, showLogo = true }: BalanceBarProps) => {
    const { wallet } = useStore();
    const { t } = useTranslation('rules');
    const { t: tNav } = useTranslation('nav');
    const { impactOccurred } = useHapticFeedback();
    const [purchaseCount, setPurchaseCount] = useState<number | null>(null);

    useEffect(() => {
        if (!onPurchasesClick) return;
        const load = async () => {
            try {
                const res = await usernameMarketApi.myPurchases();
                if (!res.ok) return;
                const d = await res.json();
                if (d?.success) setPurchaseCount(d.counts?.total ?? 0);
            } catch (e) {}
        };
        load();
        const refresh = () => load();
        socket.on('market_update', refresh);
        socket.on('push_usernames_updated', refresh);
        socket.on('username_listed', refresh);
        return () => {
            socket.off('market_update', refresh);
            socket.off('push_usernames_updated', refresh);
            socket.off('username_listed', refresh);
        };
    }, [onPurchasesClick]);

    const formatBalance = (num: number) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
        return num.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    };

    return (
        <div className={`flex items-center px-1 ${onRulesClick || showLogo || onPurchasesClick ? 'justify-between' : 'justify-end'}`}>
            {/* Left Side: Rules Glass Button OR App Logo */}
            {onRulesClick ? (
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.94 }}
                    onClick={() => {
                        impactOccurred('medium');
                        onRulesClick();
                    }}
                    className="glass flex items-center gap-1.5 px-3 py-2 rounded-2xl active:bg-white/[0.08] transition-all cursor-pointer border border-white/10 shadow-md group"
                >
                    <AlertCircle className="w-4 h-4 text-blue-400 group-hover:text-blue-300 transition-colors shrink-0" />
                    <span className="text-xs font-black text-white tracking-tight leading-none">{t('title')}</span>
                </motion.button>
            ) : showLogo ? (
                <div className="flex items-center gap-1">
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                            impactOccurred('medium');
                            onHistoryClick?.();
                        }}
                        className="glass w-10 h-10 rounded-2xl flex items-center justify-center active:bg-white/[0.06] transition-colors cursor-pointer overflow-hidden"
                    >
                        {logoIcon ? (
                            logoIcon
                        ) : logoVariant === 'lottie' ? (
                            <LottieAppIcon className="w-9 h-9" active={logoActive} />
                        ) : (
                            <img
                                src="/logo.png"
                                alt="Kite Logo"
                                className="w-[26px] h-[26px] object-contain"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.parentElement!.innerHTML = '<span class="text-xl text-blue-500 font-black italic">K</span>';
                                }}
                            />
                        )}
                    </motion.div>
                </div>
            ) : null}

            {/* Middle: White basket with purchased-count badge */}
            {onPurchasesClick && (
                <motion.button
                    whileTap={{ scale: 0.92 }}
                    onClick={() => {
                        impactOccurred('medium');
                        onPurchasesClick();
                    }}
                    className="glass w-10 h-10 rounded-2xl flex items-center justify-center active:bg-white/[0.08] transition-colors cursor-pointer relative"
                    aria-label={tNav('purchases')}
                >
                    <ShoppingBasket size={19} className="text-white" />
                    {purchaseCount != null && purchaseCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[9px] font-black flex items-center justify-center border border-white/30 shadow-[0_2px_6px_rgba(239,68,68,0.4)] leading-none">
                            {purchaseCount > 99 ? '99+' : purchaseCount}
                        </span>
                    )}
                </motion.button>
            )}

            {/* Integrated Balance & Deposit Button (Right Corner) */}
            <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                    impactOccurred('medium');
                    onDepositClick();
                }}
                className="glass-inset flex items-center gap-2.5 px-4 py-2 rounded-2xl active:bg-white/[0.06] transition-colors"
            >
                <div className="flex items-center gap-2">
                    <GramIcon className="w-5 h-5" />
                    <span className="text-[15px] font-black text-tg-text tracking-tighter leading-none">
                        {formatBalance(wallet.available_balance)}
                    </span>
                </div>

                <div className="flex items-center justify-center ml-0.5">
                    <Plus size={15} strokeWidth={4} className="text-tg-hint" />
                </div>
            </motion.button>
        </div>
    );
});
